import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { getFormattedDateTime } from "../../assets/Utils";
import '../../styles/GlucoseWidget.css';

const GlucoseWidget = ({ admissionId }) => {
  const [glucose, setGlucose] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const isMounted = React.useRef(true);

  const fetchGlucose = async () => {
    try {
      const response = await axios.get(`${process.env.REACT_APP_SERVER_BASE_URL}/hospital-portal/api/admissions/glucose-logs?admission_id=${admissionId}`);
      if (isMounted.current) {
        setGlucose(response.data);
        setError(null);
      }
    } catch (err) {
      console.error('Error fetching glucose:', err);
      if (isMounted.current) {
        setError('Failed to load glucose');
      }
    } finally {
      if (isMounted.current) {
        setLoading(false);
      }
    }
  };

  useEffect(() => {
    // Reset isMounted to true when effect runs
    isMounted.current = true;
    
    if (admissionId) {
      fetchGlucose();
      
      const intervalId = setInterval(() => {
        if (isMounted.current) {
          fetchGlucose();
        }
      }, 5 * 60 * 1000); // 5 minutes

      return () => {
        isMounted.current = false;
        clearInterval(intervalId);
      };
    }
  }, [admissionId]);

  const getTrend = () => {
    if (!glucose || glucose.length < 2) return null;
    
    const latest = glucose[0];
    const previous = glucose[1];
    
    if (!latest.glucose_level || !previous.glucose_level) return null;
    
    const diff = latest.glucose_level - previous.glucose_level;
    
    const percentChange = Math.abs(diff / previous.glucose_level * 100);
    
    if (Math.abs(diff) <= 0.5) return { direction: 'stable', icon: '→', color: '#6c757d' };
    if (diff > 0) return { direction: 'rising', icon: '↗', color: '#dc3545', change: `+${diff.toFixed(2)}` };
    return { direction: 'falling', icon: '↘', color: '#28a745', change: `${diff.toFixed(2)}` };
  };

  const getGlucoseStatus = (value) => {
    if (!value) return { status: 'Unknown', color: '#6c757d' };
    if (value < 3.9) return { status: 'Low', color: '#dc3545' };
    if (value > 11.1) return { status: 'High', color: '#dc3545' };
    if (value > 6) return { status: 'Elevated', color: '#ffc107' };
    return { status: 'In-Range', color: '#28a745' };
  };

  const getInsulinRecommendation = (value) => {
    if (!value) return 'No recommendation';
    if (value <= 6.0) return 'No Insulin, Monitor Once per day';
    if (value <= 9.0) return '2 Units, Monitor Once per day';
    if (value <= 12.0) return '4 Units, Monitor Once per shift';
    if (value <= 15.0) return '6 Units, Monitor every 8 hours';
    if (value <= 18.0) return '8 Units, Monitor every 4 hours';
    if (value <= 20.0) return '10 Units, Monitor every 2 hours';
    return '10 Units IV+ units SC Inform doctor immediately!, Monitor every hour';
  };

  if (loading) return <div className="glucose-widget">Loading glucose...</div>;
  if (error) return <div className="glucose-widget">Error: {error}</div>;
  if (!glucose || glucose.length === 0) {
    return (
      <div className="glucose-widget">
        <h4>Latest Glucose</h4>
        <p>No glucose readings recorded</p>
      </div>
    );
  }

  const latestGlucose = glucose[0];
  const glucoseDate = new Date(latestGlucose.createdAt);
  const now = new Date();
  const hoursDiff = (now - glucoseDate) / (1000 * 60 * 60);
  const trend = getTrend();
  const status = getGlucoseStatus(latestGlucose.glucose_level);

  return (
    <div className="glucose-widget">
      <h4>Latest Glucose</h4>
      
      {/* Time status message */}
      <div className={`time-status ${hoursDiff > 6 ? 'overdue' : ''}`}>
        {hoursDiff > 6 ? (
          <span>Glucose reading is over 6 hours old!</span>
        ) : (
          <span>Taken at: {getFormattedDateTime(latestGlucose.createdAt)}</span>
        )}
      </div>

      {/* Glucose value section */}
      <div className="glucose-value-container">
        <div className="glucose-main">
          <div className="glucose-value">
            <strong>{latestGlucose.glucose_level || 'N/A'}</strong>
            <span className="unit">mmol/L</span>
          </div>
          
          <div 
            className="glucose-status"
            style={{ color: status.color }}
          >
            {status.status}
          </div>
          
          <div className="insulin-recommendation">
            Insulin: {getInsulinRecommendation(latestGlucose.glucose_level)}
          </div>
        </div>
        
        {trend && (
          <div className="glucose-trend">
            <span 
              className="trend-icon"
              style={{ color: trend.color }}
            >
              {trend.icon}
            </span>
            {trend.change && (
              <span className="trend-change">{trend.change}</span>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default GlucoseWidget;
